import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = anonymous, object = signed-in
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Track whether the user ever had an authenticated session. Only THEN
  // does "your session expired" make sense. A brand-new visitor with no
  // cookies must never see that toast.
  const hadSession = useRef(false);

  const refresh = useCallback(async () => {
    try {
      // Silent auth-check: a 401 here means "anonymous visitor" and must
      // NOT broadcast summit:session-expired (that would race with a
      // subsequent successful login and toast "Session expired" on the
      // /dashboard we just navigated to).
      const { data } = await api.get("/auth/me", { _silentAuthCheck: true });
      setUser(data);
      hadSession.current = true;
    } catch (_e) {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Global session-expired handler.
  //
  // The interceptor dispatches `summit:session-expired` on every failed
  // /auth/refresh — including the FIRST-EVER pageload of a logged-out
  // visitor (they have no refresh_token cookie, so the refresh attempt
  // 401s). That doesn't mean the user's session expired — it means they
  // never had one. We must NOT show the toast in that case.
  useEffect(() => {
    const PROTECTED_PREFIXES = [
      "/dashboard", "/account", "/admin", "/courses", "/articles",
      "/resources", "/ai-coach", "/certificates", "/checkout", "/quizzes",
      "/lessons",
    ];
    const handler = (event) => {
      const p = location.pathname;
      const isProtected = PROTECTED_PREFIXES.some((pref) => p.startsWith(pref));
      const wasSignedIn = hadSession.current;
      const reason = event?.detail;
      setUser(false);
      hadSession.current = false;

      if (!isProtected) return;
      if (wasSignedIn) {
        if (reason === "session_revoked") {
          toast.error("Signed out — this account was opened on another device. If it wasn't you, change your password immediately.", { duration: 8000 });
        } else if (reason === "account_suspended") {
          toast.error("Your account has been suspended. Contact support to restore access.", { duration: 10000 });
        } else {
          toast.error("Your session expired. Please sign in again.");
        }
      }
      navigate(`/login?redirect=${encodeURIComponent(p)}`);
    };
    window.addEventListener("summit:session-expired", handler);
    return () => window.removeEventListener("summit:session-expired", handler);
  }, [navigate, location.pathname]);

  const login = useCallback(async (email, password, remember = false) => {
    const { data } = await api.post("/auth/login", { email, password, remember });
    // OTP challenge — caller renders the code input step and finishes with verifyOtp.
    if (data.otp_required) {
      return { otp_required: true, otp_challenge: data.otp_challenge, email_masked: data.email_masked };
    }
    setUser(data.user);
    hadSession.current = true;
    return data.user;
  }, []);

  const verifyOtp = useCallback(async (otp_challenge, code, remember = false) => {
    const { data } = await api.post("/auth/verify-otp", { otp_challenge, code, remember });
    setUser(data.user);
    hadSession.current = true;
    return data.user;
  }, []);

  const register = useCallback(async (name, email, password, preferred_language = "en", ref = null) => {
    const payload = { name, email, password, preferred_language };
    if (ref) payload.ref = ref;
    const { data } = await api.post("/auth/register", payload);
    setUser(data.user);
    hadSession.current = true;
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.warn("Logout network call failed; clearing local session anyway.", err);
      }
    }
    setUser(false);
    hadSession.current = false;
  }, []);

  const updateProfile = useCallback(async (payload) => {
    const { data } = await api.patch("/auth/me", payload);
    setUser(data);
    return data;
  }, []);

  const value = useMemo(
    () => ({ user, login, verifyOtp, register, logout, refresh, updateProfile, formatApiError, setUser }),
    [user, login, verifyOtp, register, logout, refresh, updateProfile],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
